import {
  HStack,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import React from "react";
import { RiMagicLine } from "react-icons/ri";
import type { Point } from "src/components/DocumentViewer/V2/types";
import { AIValidationResult } from "src/hooks/useDocumentAI";
import {
  IdentityAnalyzeIdMatch,
  Profile,
  ProfileType,
} from "../utils/analyzeIdFormMatchUtils";
import { ParentPanel } from "./ParentPanel";
import { SiblingsPanel } from "./SiblingsPanel";
import { StudentPanel } from "./StudentPanel";

function FamilyTabLabel({
  label,
  showMagicIcon,
}: {
  label: string;
  showMagicIcon: boolean;
}) {
  return (
    <HStack as="span" spacing={2} display="inline-flex" alignItems="center">
      <span>{label}</span>
      {showMagicIcon ? (
        <Icon as={RiMagicLine} color="purple.500" boxSize={4} flexShrink={0} />
      ) : null}
    </HStack>
  );
}

type FamilyTabsProps = {
  profiles: Profile[];
  formId: string;
  aiResultsMap?: Record<string, AIValidationResult> | null;
  identityAiProfileMatches: IdentityAnalyzeIdMatch[];
  onFindInDocument?: (polygons: readonly (readonly Point[])[]) => void;
};

export const FamilyTabs: React.FC<FamilyTabsProps> = ({
  profiles,
  formId,
  aiResultsMap,
  identityAiProfileMatches,
  onFindInDocument,
}) => {
  const student = profiles.find(
    (profile) => profile.person_type === ProfileType.Student
  );
  const studentMatches = identityAiProfileMatches.filter(
    (match) => match.id === student?.id
  );

  const parents = profiles.filter(
    (profile) => profile.person_type === ProfileType.Parent
  );
  const parentMatches = identityAiProfileMatches.filter((match) =>
    parents.some((parent) => parent.id === match.id)
  );

  const siblings = profiles.filter(
    (profile) => profile.person_type === ProfileType.Sibling
  );
  const siblingsMatches = identityAiProfileMatches.filter((match) =>
    siblings.some((sibling) => sibling.id === match.id)
  );

  return (
    <Tabs variant="line" size="sm" colorScheme="primary">
      <TabList
        position="sticky"
        top={0}
        zIndex={1}
        bg="gray.50"
        borderBottomWidth="1px"
        borderBottomColor="gray.300"
      >
        <Tab
          py={4}
          px={8}
          fontWeight="medium"
          fontSize="md"
          _selected={{
            color: "primary.600",
            fontWeight: "semibold",
            borderColor: "primary.600",
          }}
        >
          <FamilyTabLabel
            label="Student"
            showMagicIcon={studentMatches.length > 0}
          />
        </Tab>
        <Tab
          py={4}
          px={8}
          fontWeight="medium"
          fontSize="md"
          _selected={{
            color: "primary.600",
            fontWeight: "semibold",
            borderColor: "primary.600",
          }}
        >
          <FamilyTabLabel
            label="Parent"
            showMagicIcon={parentMatches.length > 0}
          />
        </Tab>
        <Tab
          py={4}
          px={8}
          fontWeight="medium"
          fontSize="md"
          _selected={{
            color: "primary.600",
            fontWeight: "semibold",
            borderColor: "primary.600",
          }}
        >
          <FamilyTabLabel
            label="Siblings"
            showMagicIcon={siblingsMatches.length > 0}
          />
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={8} py={6}>
          <StudentPanel
            person={student}
            aiResultsMap={aiResultsMap}
            identityAiProfileMatches={studentMatches}
            onFindInDocument={onFindInDocument}
          />
        </TabPanel>
        <TabPanel px={8} py={6}>
          <ParentPanel
            parents={parents}
            formId={formId}
            aiResultsMap={aiResultsMap}
            identityAiProfileMatches={parentMatches}
            onFindInDocument={onFindInDocument}
          />
        </TabPanel>
        <TabPanel px={8} py={6}>
          <SiblingsPanel
            siblings={siblings}
            identityAiProfileMatches={siblingsMatches}
            onFindInDocument={onFindInDocument}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};
